import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Header } from '../components/header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Ticket,
  Search,
  X,
  MapPin,
  Info,
  Navigation,
  Phone,
} from 'lucide-react';
import { useTranslationWithLanguage } from '../context/language-context';
import { InspectionCard } from '../components/inspection-card';
import { useAuth } from 'react-oidc-context';

// Define types for inspections
interface Inspection {
  id: string; // Changed to string to match API's task_id
  summary: string;
  description: string;
  emirate_id: number;
  emirate: {
    id: number;
    name_en: string;
    name_ar: string;
  };
  establishment_id: string;
  establishment: {
    id: string;
    name_en: string;
    name_ar: string;
  };
  assigned_to: string;
  assigned_to_user: {
    id: string;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
  };
  inspection_type_id: number;
  inspection_type: {
    id: number;
    name_en: string;
    name_ar: string;
  };
  risk_score: number;
  risk_grade: string;
  status: string;
  hours: string;
  location: {
    lat: number;
    lng: number;
  };
}

// Base URL from environment variables
const BASE_URL = import.meta.env.VITE_BASE_URL;

// Helper function to calculate risk grade
const calculateRiskGrade = (score: any): string => {
  if (score.params.risk_category) return score.params.risk_category;
  return 'HIGH';
};

// Helper function to map API status to component status
const mapStatus = (statusDisplay: string): string => {
  switch (statusDisplay) {
    case 'Assigned':
      return 'assigned';
    case 'ASSIGNED INSPECTOR':
      return 'assigned';
    case 'In Progress':
      return 'inProgress';
    case 'PAUSED':
      return 'paused';
    case 'COMPLETED':
      return 'completed';
    // Add more mappings based on possible API statuses
    default:
      return 'pool'; // Fallback, adjust based on API documentation
  }
};

// Function to get risk color based on grade
const getRiskColor = (grade: string): string => {
  switch (grade) {
    case 'A':
      return 'bg-green-800';
    case 'B':
      return 'bg-green-600';
    case 'C':
      return 'bg-yellow-500';
    case 'D':
      return 'bg-orange-500';
    case 'E':
      return 'bg-red-600';
    case 'F':
      return 'bg-red-800';
    default:
      return 'bg-gray-500';
  }
};

const InspectionsPage = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { t, language } = useTranslationWithLanguage();
  const [activeFilter, setActiveFilter] = useState('pool');
  const [searchQuery, setSearchQuery] = useState('');
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0); // Track total available items
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Define fetchInspectionsData to fetch all data at once
  const fetchInspectionsData = useCallback(async () => {
    try {
      setIsLoading(true);

      // Build condition parameter based on activeFilter
      let conditionParam = {};

      if (activeFilter === 'inProgress') {
        // For active tab, filter by task_status_id = 14
        conditionParam = {
          rules: [
            {
              operator: 'eq',
              field: 'task_status_id',
              value: '14',
            },
          ],
        };
        console.log(
          'Filtering for inProgress tasks with task_status_id = 14'
        );
      } else if (activeFilter === 'assigned') {
        // For assigned tab, filter by task_status_id = 6
        conditionParam = {
          rules: [
            { operator: 'eq', field: 'task_status_id', value: '6' },
          ],
        };
      } else if (activeFilter === 'paused') {
        // For paused tab, filter by task_status_id = 15
        conditionParam = {
          rules: [
            { operator: 'eq', field: 'task_status_id', value: '10' },
          ],
        };
      } else if (activeFilter === 'completed') {
        // For completed tab, filter by task_status_id = 16
        conditionParam = {
          rules: [
            { operator: 'eq', field: 'task_status_id', value: '15' },
          ],
        };
      } else if (activeFilter === 'pool') {
        // For 'pool' tab, filter by task_status_id = 1, 5, 9, 11
        conditionParam = {
          rules: [
            {
              operator: 'in',
              field: 'task_status_id',
              value: ['2'],
            },
          ],
        };
      }

      // Build globalFilter parameter if search query exists
      let globalFilterParam = {};
      if (searchQuery) {
        globalFilterParam = {
          searchTerm: searchQuery,
          searchFields: [
            'establishment.name_en',
            'establishment_id',
            'inspection_type.name_en',
          ],
        };
      }

      // Build URL with query parameters - fetch all data at once with a large page size
      let url = `${BASE_URL}/jsonapi/transform/task_list?sortBy=created_at&sortOrder=desc&page=1&pageSize=100`;

      console.log(`Building URL to fetch all data at once`);
      console.log(`Final URL: ${url}`);

      // Add condition parameter if it has rules
      if (Object.keys(conditionParam).length > 0) {
        url += `&condition=${encodeURIComponent(
          JSON.stringify(conditionParam)
        )}`;
      }

      // Add globalFilter parameter if it has a searchTerm
      if (Object.keys(globalFilterParam).length > 0) {
        url += `&globalFilter=${encodeURIComponent(
          JSON.stringify(globalFilterParam)
        )}`;
      }

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${auth?.user?.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      // Log the API response for debugging
      console.log(`API Response:`, data);

      // Set total items count
      if (data.total !== undefined) {
        setTotalItems(data.total);
      }

      const mappedInspections = data.data.map((item: any) => ({
        id: item.task_id,
        summary: `Routine inspection with risk: ${
          item.risk_score || 0
        }`,
        description:
          item.params?.message || 'No description available',
        emirate_id: item.emirate_id,
        emirate: {
          id: item.emirate_id,
          name_en: item.emirate_display || '',
          name_ar: item.emirate_display || '',
        },
        establishment_id: item.establishment_id,
        establishment: {
          id: item.establishment_id,
          name_en: item.establishment?.name_en || '',
          name_ar: item.establishment?.name_ar || '',
        },
        assigned_to: item.assigned_to || '',
        assigned_to_user: {
          id: item.assigned_to || '',
          username: item.assigned_to || 'Unknown',
          email: '',
          first_name: '',
          last_name: '',
        },
        inspection_type_id: item.params?.inspection_type_id
          ? parseInt(item.params.inspection_type_id)
          : 0,
        inspection_type: {
          id: item.params?.inspection_type_id
            ? parseInt(item.params.inspection_type_id)
            : 0,
          name_en: item.params?.inspection_type_display || '',
          name_ar: item.params?.inspection_type_display || '', // Arabic not provided
        },
        risk_score: item.risk_score || 0,
        risk_grade: calculateRiskGrade(item || 0),
        status: mapStatus(item.task_status_display || ''),
        hours: '',
        location: {
          lat: item.establishment?.latitude
            ? parseFloat(item.establishment.latitude)
            : 0,
          lng: item.establishment?.longitude
            ? parseFloat(item.establishment.longitude)
            : 0,
        },
      }));

      console.log(`Mapped inspections:`, mappedInspections);
      setInspections(mappedInspections);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error('Failed to fetch inspections:', error);
      setInspections([]);
    }
  }, [activeFilter, searchQuery, auth?.user?.access_token]);

  // Fetch inspections data when activeFilter or searchQuery changes
  useEffect(() => {
    setIsLoading(true);
    setInspections([]); // Clear existing inspections
    setTotalItems(0); // Reset total items count

    // Use the fetchInspectionsData function from useCallback
    fetchInspectionsData().catch((error) => {
      console.error('Failed to fetch inspections:', error);
      setInspections([]);
    });
  }, [activeFilter, searchQuery, fetchInspectionsData]);

  // Listen for inspection:assigned event
  useEffect(() => {
    // Handler for when an inspection is assigned
    const handleInspectionAssigned = () => {
      // Navigate to assigned tab
      setActiveFilter('assigned');
      // Data will be refreshed by the above useEffect when activeFilter changes
    };

    // Handler for when an inspection is started
    const handleInspectionStarted = () => {
      // Navigate to inProgress tab (Active)
      setActiveFilter('inProgress');
      // Data will be refreshed by the above useEffect when activeFilter changes
    };

    // Add event listeners
    window.addEventListener(
      'inspection:assigned',
      handleInspectionAssigned
    );
    window.addEventListener(
      'inspection:started',
      handleInspectionStarted
    );

    // Clean up event listeners
    return () => {
      window.removeEventListener(
        'inspection:assigned',
        handleInspectionAssigned
      );
      window.removeEventListener(
        'inspection:started',
        handleInspectionStarted
      );
    };
  }, []);

  // No need for client-side filtering since we're using API filtering
  const filteredInspections = inspections;

  return (
    <div
      className="flex flex-col min-h-screen w-full"
      dir={language === 'ar' ? 'rtl' : 'ltr'}
    >
      <Header
        title={t('inspections.myInspections')}
        isRTL={language === 'ar'}
      />
      <div className="p-4">
        {/* Search input */}
        <div className="mb-4 ipad-mini:max-w-sm surface-pro:max-w-md ipad:max-w-md ipad-pro:max-w-lg ipad-pro-large:max-w-xl ipad-mini:mx-auto">
          <div className="relative">
            <Input
              placeholder={t('inspections.searchInspections')}
              className={`${
                language === 'ar' ? 'pr-9' : 'pl-9'
              } py-2 rounded-full border border-gray-300 h-10`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Search
              className={`absolute ${
                language === 'ar' ? 'right-3' : 'left-3'
              } top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500`}
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                className={`absolute ${
                  language === 'ar' ? 'left-2' : 'right-2'
                } top-1/2 transform -translate-y-1/2 h-6 w-6`}
                onClick={() => setSearchQuery('')}
              >
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex justify-between mb-4 bg-gray-100 p-1 rounded-full ipad-mini:max-w-sm surface-pro:max-w-md ipad:max-w-md ipad-pro:max-w-lg ipad-pro-large:max-w-xl ipad-mini:mx-auto overflow-x-auto">
          <Button
            variant="ghost"
            className={`rounded-full h-8 text-xs flex items-center justify-center w-[85px] ${
              activeFilter === 'pool'
                ? 'bg-[#b89256] text-white hover:opacity-90'
                : 'text-gray-600 hover:bg-gray-200'
            }`}
            onClick={() => setActiveFilter('pool')}
          >
            <span className="whitespace-nowrap">
              {t('filters.pool')}
            </span>
          </Button>
          <Button
            variant="ghost"
            className={`rounded-full h-8 text-xs flex items-center justify-center w-[85px] ${
              activeFilter === 'assigned'
                ? 'bg-[#b89256] text-white hover:opacity-90'
                : 'text-gray-600 hover:bg-gray-200'
            }`}
            onClick={() => setActiveFilter('assigned')}
          >
            <span className="whitespace-nowrap">
              {t('filters.assigned')}
            </span>
          </Button>
          <Button
            variant="ghost"
            className={`rounded-full h-8 text-xs flex items-center justify-center w-[85px] ${
              activeFilter === 'inProgress'
                ? 'bg-[#b89256] text-white hover:opacity-90'
                : 'text-gray-600 hover:bg-gray-200'
            }`}
            onClick={() => setActiveFilter('inProgress')}
          >
            <span className="whitespace-nowrap">
              {t('filters.active')}
            </span>
          </Button>
          <Button
            variant="ghost"
            className={`rounded-full h-8 text-xs flex items-center justify-center w-[85px] ${
              activeFilter === 'paused'
                ? 'bg-[#b89256] text-white hover:opacity-90'
                : 'text-gray-600 hover:bg-gray-200'
            }`}
            onClick={() => setActiveFilter('paused')}
          >
            <span className="whitespace-nowrap">
              {t('filters.paused')}
            </span>
          </Button>
          <Button
            variant="ghost"
            className={`rounded-full h-8 text-xs flex items-center justify-center w-[85px] ${
              activeFilter === 'completed'
                ? 'bg-[#b89256] text-white hover:opacity-90'
                : 'text-gray-600 hover:bg-gray-200'
            }`}
            onClick={() => setActiveFilter('completed')}
          >
            <span className="whitespace-nowrap">
              {t('filters.completed')}
            </span>
          </Button>
        </div>

        {/* Loading state, establishment cards, or empty state */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)]">
            <div className="w-12 h-12 border-4 border-[#b89256] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600 font-medium">
              {t('common.loading')}
            </p>
          </div>
        ) : filteredInspections.length > 0 ? (
          <div
            ref={scrollContainerRef}
            className="flex flex-col overflow-y-auto pb-16 gap-3 min-h-[300px] max-h-[calc(100vh-200px)] no-scrollbar ipad-mini:grid ipad-mini:grid-cols-1 surface-pro:grid-cols-2 ipad:grid-cols-2 ipad-pro:grid-cols-3 ipad-pro-large:grid-cols-4 ipad-mini:gap-4"
          >
            {filteredInspections.map((item) => {
              console.log(
                `Rendering card for item ${item.id} with status ${item.status}`
              );
              return (
                <div key={item.id} className="w-full">
                  <InspectionCard
                    id={item.id}
                    establishment_id={item.establishment_id}
                    establishment={item.establishment}
                    emirate={item.emirate}
                    inspection_type={item.inspection_type}
                    risk_grade={item.risk_grade}
                    status={item.status}
                    language={language}
                  />
                </div>
              );
            })}

            {/* Progress indicator */}
            {totalItems > 0 && (
              <div className="col-span-full flex justify-center py-2 text-sm text-gray-500">
                {t('inspections.showingItems', {
                  loaded: filteredInspections.length,
                  total: totalItems,
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center text-center mt-8">
            <Ticket className="w-12 h-12 text-[#b89256] mb-4" />
            <h2 className="text-2xl font-bold text-[#b89256] mb-2">
              {t('inspections.noInspectionsFound')}
            </h2>
            <p className="text-gray-500 mb-6">
              {t('plan.tryAdjustingSearch')}
            </p>
            <div className="w-full max-w-xs mx-auto mb-6">
              <img
                src="/customer360/placeholder.svg"
                width={300}
                height={300}
                alt="Inspection illustration"
                className="mx-auto"
              />
            </div>
            {/* <Link to="/services">
              <Button className="rounded-full bg-[#b89256] text-white hover:opacity-90 w-[180px]">
                <Search className="mr-2 h-4 w-4" />
                {t('inspections.findInspectionServices')}
              </Button>
            </Link> */}
          </div>
        )}
      </div>
    </div>
  );
};

export default InspectionsPage;
